import { useCallback, useEffect, useRef, useState } from 'react'
import { BrandVoiceFileError, validateBrandVoiceFile } from './brandVoiceUpload'
import useBrandVoiceUpload, {
  BrandVoiceUploadErrorCode,
} from './useBrandVoiceUpload'

// Shared "Generate from documents" state machine for the two brand-voice hosts
// (the Settings pane + the Agent-Tuning modal). Both used to declare this whole
// machine — staged files, client validation, deriveError, the mount guard, and
// the derive orchestration — independently; that duplication is what let the
// deriveError/seedWasBlank fixes drift between the two copies. Owning it once
// here keeps them in lock-step. Each host supplies only what to DO with the
// derived text (`onDerived`) and renders its own chrome.
//
// The errorCode -> locale-key mapping is decision logic, not chrome, so it's
// computed here too (`errorCopyKey`) rather than re-derived as a ternary in
// each host — same drift risk as the rest of the machine.
export type BrandVoiceErrorCopyKey =
  | 'generate_error_unreadable'
  | 'generate_error_too_thin'
  | 'generate_error_retryable'

export type UseBrandVoiceGenerate = {
  // Staged files (single-file v1 — the array is kept so lifting the cap later is
  // a one-line change with no re-layout).
  files: File[]
  // Client-side validation error for the last add attempt (wrong type/size).
  clientError: BrandVoiceFileError | null
  // The last derive attempt failed (drives the host's error banner).
  deriveError: boolean
  // Busy for the WHOLE generate window — presign + S3 PUT ('uploading') AND the
  // derive POST ('deriving') — so the host locks Save + shows the skeleton for
  // the entire window, not just the final POST.
  isDeriving: boolean
  // The failure class of the last derive, so the host can pick honest copy:
  // 'unreadable'/'content_too_thin' blame the document (two different remedies);
  // 'upload'/'derive' are our-end faults.
  errorCode: BrandVoiceUploadErrorCode | null
  // The locale key for `errorCode`, honest-copy mapping computed once here
  // (see the type doc above). Only meaningful when `deriveError` is true.
  errorCopyKey: BrandVoiceErrorCopyKey
  addFiles: (files: FileList | null) => void
  removeFile: (index: number) => void
  clearClientError: () => void
  clearDeriveError: () => void
  // Run the derive for the staged file; on success hand the derived text to
  // `onDerived` and clear the staged file. No-op with no staged file.
  generate: (onDerived: (derived: string) => void) => Promise<void>
  // Abort an in-flight derive: drop its eventual result and return the host to
  // an editable state (the only escape on the pane, where there's no modal
  // Cancel). Keeps the staged file so the merchant can retry.
  cancel: () => void
  // Full teardown: clear staged files, both errors, and the underlying mutation.
  reset: () => void
}

const useBrandVoiceGenerate = (): UseBrandVoiceGenerate => {
  const {
    upload,
    status,
    errorCode,
    reset: resetUpload,
  } = useBrandVoiceUpload()
  const [files, setFiles] = useState<File[]>([])
  const [clientError, setClientError] = useState<BrandVoiceFileError | null>(
    null
  )
  const [deriveError, setDeriveError] = useState(false)

  const isDeriving = status === 'uploading' || status === 'deriving'
  const errorCopyKey: BrandVoiceErrorCopyKey =
    errorCode === 'unreadable'
      ? 'generate_error_unreadable'
      : errorCode === 'content_too_thin'
      ? 'generate_error_too_thin'
      : 'generate_error_retryable'

  // Skip the post-await writes if the host unmounted (tab switch / modal close)
  // mid-derive — the underlying hook guards its OWN state, not this orchestration.
  const isMountedRef = useRef(true)
  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
    }
  }, [])
  // Set when the merchant aborts an in-flight derive: its settled result is
  // dropped rather than slammed into the (now editable) textarea.
  const cancelledRef = useRef(false)

  const addFiles = useCallback((incoming: FileList | null) => {
    const file = incoming?.[0]
    if (!file) return
    const error = validateBrandVoiceFile(file)
    if (error) {
      setClientError(error)
      return
    }
    setClientError(null)
    // Single-file v1 (Q3): replace, never append.
    setFiles([file])
  }, [])

  const removeFile = useCallback((index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
  }, [])

  const clearClientError = useCallback(() => setClientError(null), [])
  const clearDeriveError = useCallback(() => setDeriveError(false), [])

  const generate = useCallback(
    async (onDerived: (derived: string) => void): Promise<void> => {
      const file = files[0]
      if (!file) return
      setDeriveError(false)
      cancelledRef.current = false
      const derived = await upload(file)
      // Dropped if the host unmounted OR the merchant cancelled mid-derive.
      if (!isMountedRef.current || cancelledRef.current) return
      if (derived) {
        onDerived(derived)
        setFiles([])
      } else {
        // unreadable / content_too_thin / derive / upload — keep the previous text intact.
        setDeriveError(true)
      }
    },
    [files, upload]
  )

  const cancel = useCallback(() => {
    // resetUpload() flips status → idle so isDeriving clears immediately; the
    // underlying request still settles, but generate()'s guard discards it.
    cancelledRef.current = true
    resetUpload()
    setDeriveError(false)
  }, [resetUpload])

  const reset = useCallback(() => {
    cancelledRef.current = true
    setFiles([])
    setClientError(null)
    setDeriveError(false)
    resetUpload()
  }, [resetUpload])

  return {
    files,
    clientError,
    deriveError,
    isDeriving,
    errorCode,
    errorCopyKey,
    addFiles,
    removeFile,
    clearClientError,
    clearDeriveError,
    generate,
    cancel,
    reset,
  }
}

export default useBrandVoiceGenerate
